import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import type { AppTheme } from '../theme';

export type UserMenuProps = {
  fullName: string;
  email?: string;
  organisationName?: string;
  pictureUrl?: string;
  theme: AppTheme;
  onOpenProfile: () => void;
  onOpenPurchases: () => void;
  onThemeChange: (theme: AppTheme) => void;
  onLogout: () => void | Promise<void>;
};

function initialsFor(fullName: string, email?: string): string {
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
  if (nameParts.length > 1) {
    return `${nameParts[0]![0] ?? ''}${nameParts.at(-1)![0] ?? ''}`.toUpperCase();
  }

  const source = nameParts[0] || email?.split('@')[0] || 'User';
  return source.slice(0, 2).toUpperCase();
}

export function UserMenu({
  fullName,
  email,
  organisationName,
  pictureUrl,
  theme,
  onOpenProfile,
  onOpenPurchases,
  onThemeChange,
  onLogout,
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [pictureUnavailable, setPictureUnavailable] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<'first' | 'last'>('first');
  const openedByKeyboard = useRef(false);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuId = useId();
  const displayName = fullName.trim() || email || 'Account';
  const initials = useMemo(() => initialsFor(fullName, email), [email, fullName]);
  const nextTheme: AppTheme = theme === 'dark' ? 'light' : 'dark';
  const themeLabel = nextTheme === 'dark' ? 'Dark mode' : 'Light mode';

  useEffect(() => {
    setPictureUnavailable(false);
  }, [pictureUrl]);

  useEffect(() => {
    if (!open) return;

    if (openedByKeyboard.current) {
      const menuItems = rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
      const itemToFocus =
        pendingFocusRef.current === 'last'
          ? menuItems?.item((menuItems?.length ?? 1) - 1)
          : menuItems?.item(0);
      itemToFocus?.focus();
      openedByKeyboard.current = false;
    }

    const closeOnOutsidePointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const openMenu = (focus: 'first' | 'last' = 'first') => {
    pendingFocusRef.current = focus;
    openedByKeyboard.current = true;
    setOpen(true);
  };

  const runAction = (action: () => void | Promise<void>) => {
    setOpen(false);
    triggerRef.current?.focus();
    void action();
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const menuItems = [
      ...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
    ];
    if (menuItems.length === 0) return;

    const currentIndex = menuItems.findIndex((item) => item === document.activeElement);
    if (event.key === 'Home') {
      menuItems[0]!.focus();
      return;
    }
    if (event.key === 'End') {
      menuItems.at(-1)!.focus();
      return;
    }

    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + direction + menuItems.length) % menuItems.length;
    menuItems[nextIndex]!.focus();
  };

  const handleMouseEnter = useCallback(() => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverCloseTimer.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  }, []);

  return (
    <div
      className="user-menu"
      ref={rootRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${open ? 'Close' : 'Open'} user menu for ${displayName}`}
        className="user-menu-trigger"
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            openMenu('first');
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            openMenu('last');
          }
        }}
      >
        <span className="user-menu-avatar" aria-hidden="true">
          {pictureUrl && !pictureUnavailable ? (
            <img
              alt=""
              className="user-menu-avatar-image"
              decoding="async"
              src={pictureUrl}
              onError={() => setPictureUnavailable(true)}
            />
          ) : (
            <span className="user-menu-avatar-fallback">{initials}</span>
          )}
        </span>
        <span className="user-menu-identity">
          <strong>{displayName}</strong>
          {email && email !== displayName ? <small>{email}</small> : null}
          {organisationName ? <small>{organisationName}</small> : null}
        </span>
      </button>

      {open ? (
        <div
          aria-label={`User options for ${displayName}`}
          className="user-menu-popout"
          id={menuId}
          role="menu"
          onKeyDown={handleMenuKeyDown}
        >
          <button
            className="user-menu-item"
            role="menuitem"
            type="button"
            onClick={() => runAction(onOpenProfile)}
          >
            Profile
          </button>
          <button
            className="user-menu-item"
            role="menuitem"
            type="button"
            onClick={() => runAction(onOpenPurchases)}
          >
            Purchases
          </button>
          <button
            className="user-menu-item user-menu-theme-item"
            role="menuitem"
            type="button"
            onClick={() => runAction(() => onThemeChange(nextTheme))}
          >
            {themeLabel}
          </button>
          <button
            className="user-menu-item user-menu-logout"
            role="menuitem"
            type="button"
            onClick={() => runAction(onLogout)}
          >
            Logout
          </button>
        </div>
      ) : null}
    </div>
  );
}
